import {
  CheckCircle2,
  Headphones,
  LayoutDashboard,
  LogIn,
  LogOut,
  Moon,
  Route,
  Send,
  Settings,
  ShieldCheck,
  Store,
  Sun,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  assignDialog,
  closeDialog,
  escalateDialog,
  fetchDashboard,
  fetchDialogs,
  fetchMessages,
  fetchClientCard,
  fetchOperatorAvailability,
  fetchQueue,
  getPersistedAuthUser,
  getPersistedAvailabilityStatus,
  getPersistedCurrentView,
  isApiMock,
  loginAs,
  logoutLocal,
  persistAuthUser,
  persistAvailabilityStatus,
  persistCurrentView,
  sendMessage,
  setOperatorAvailability,
  fetchEmployeeStats,
} from './api/hubApi';
import type { EmployeeStat, EmployeeStatsResponse, ShopStatRow } from './api/hubApi';
import type { OperatorAvailabilityStatus } from './api/types';
import type {
  OperatorClientCard,
  OperatorDialog,
  OperatorMessage,
  RoleCode,
  RoleView,
} from './api/types';
import { SupervisorView } from './views/SupervisorView';
import { AdminView } from './views/AdminView';

type AppView = RoleView;

const VIEW_TABS: { view: AppView; label: string; defaultRole: RoleCode }[] = [
  { view: 'operator',   label: 'Оператор',     defaultRole: 'manager' },
  { view: 'supervisor', label: 'Руководитель', defaultRole: 'shop_supervisor' },
  { view: 'admin',      label: 'Администратор', defaultRole: 'admin' },
];

// Whether we are in mock demo mode (VITE_HUB_API_MODE=mock)
const MOCK_MODE = isApiMock();

/**
 * The set of views that a given role is allowed to access.
 *
 * PRODUCTION RBAC — strict panel separation:
 *   - Operator roles  (manager, service_manager)                 → operator only
 *   - Supervisor roles (shop_supervisor, service_supervisor,
 *                       founder, branch_admin)                   → supervisor only
 *   - Admin role       (admin)                                   → admin only
 *
 * Nobody sees panels outside their own role's panel.
 * This mirrors the backend requireRole() enforcement.
 */
function getAllowedViews(role: RoleCode): AppView[] {
  switch (role) {
    case 'manager':
    case 'service_manager':
      return ['operator'];
    case 'shop_supervisor':
    case 'service_supervisor':
    case 'founder':
    case 'branch_admin':
      return ['supervisor'];
    case 'admin':
      return ['admin'];
    default:
      return ['operator'];
  }
}

/** Default view for a given role when no persisted view is available. */
function defaultViewForRole(role: RoleCode): AppView {
  switch (role) {
    case 'manager':
    case 'service_manager':
      return 'operator';
    case 'shop_supervisor':
    case 'service_supervisor':
    case 'founder':
    case 'branch_admin':
      return 'supervisor';
    case 'admin':
      return 'admin';
    default:
      return 'operator';
  }
}

/**
 * Resolve the initial view for a user:
 *  1. Try the persisted view from localStorage.
 *  2. Validate it against the user's allowed views.
 *  3. Fall back to the role-based default.
 */
function resolveInitialView(role: RoleCode, persisted: string | null): AppView {
  const allowed = getAllowedViews(role);
  if (persisted && (allowed as string[]).includes(persisted)) {
    return persisted as AppView;
  }
  return defaultViewForRole(role);
}

/**
 * Known seed / mock operator names that must NEVER appear in the live stats
 * block when running in HTTP mode.  The set is defined at module level so the
 * dead-code-elimination pass can drop it in mock-only builds if desired.
 * In production (HUB_API_MODE=http) these names are filtered out of any API
 * response before it reaches React state, providing a belt-and-suspenders
 * defence against leftover seed data in the database.
 */
const SEED_OPERATOR_NAMES: ReadonlySet<string> = MOCK_MODE
  ? new Set<string>() // In mock mode: no filtering — all names are valid demo data.
  : new Set<string>([
      '\u0418\u0440\u0438\u043d\u0430 \u041f.', // Ирина П.
      '\u041e\u043b\u0435\u0433 \u0421.', // Олег С.
      '\u0420\u0443\u043a\u043e\u0432\u043e\u0434\u0438\u0442\u0435\u043b\u044c \u043c\u0430\u0433\u0430\u0437\u0438\u043d\u0430', // Руководитель магазина
      '\u0421\u0435\u0440\u0432\u0438\u0441', // Сервис
    ]);

// ─── LoginScreen ──────────────────────────────────────────────────────────────

interface LoginUser {
  id: string;
  name: string;
  role: RoleCode;
  shopId: string | null;
  shopName: string | null;
}

interface LoginScreenProps {
  onLogin: (user: LoginUser, token: string) => void;
}

function LoginScreen({ onLogin }: LoginScreenProps) {
  const [loginVal, setLoginVal] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!loginVal.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await loginAs(loginVal.trim(), password);
      onLogin(res.data.user as LoginUser, res.data.token);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('403')) {
          setError('Неверный логин или пароль');
        } else if (err.message.includes('503')) {
          setError('CRM временно недоступна. Попробуйте через несколько секунд.');
        } else {
          setError('Ошибка соединения с сервером. Проверьте сеть.');
        }
      } else {
        setError('Неизвестная ошибка. Попробуйте ещё раз.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen" data-testid="login-screen">
      <div className="login-card">
        <div className="brand" style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>
          <img src="./grace-logo.jpg" alt="Логотип" className="brand-logo" />
          <div>
            <p className="eyebrow">CRM Hub</p>
            <strong>Омниканальный хаб</strong>
          </div>
        </div>

        <h1 style={{ fontSize: '1.25rem', marginBottom: '1.25rem', fontWeight: 600 }}>
          Вход для сотрудников
        </h1>

        {error && (
          <div className="login-error" role="alert" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <div className="login-field">
            <label htmlFor="login-input">Логин</label>
            <input
              id="login-input"
              type="text"
              autoComplete="username"
              value={loginVal}
              onChange={(e) => setLoginVal(e.target.value)}
              placeholder="Введите логин или email"
              disabled={loading}
              data-testid="input-login"
              required
            />
          </div>
          <div className="login-field">
            <label htmlFor="password-input">Пароль</label>
            <input
              id="password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Введите пароль"
              disabled={loading}
              data-testid="input-password"
              required
            />
          </div>
          <button
            type="submit"
            className="primary-button"
            disabled={loading || !loginVal.trim() || !password}
            data-testid="button-login-submit"
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            <LogIn size={18} />
            {loading ? 'Вход…' : 'Войти'}
          </button>
        </form>

        {MOCK_MODE && (
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', opacity: 0.6, textAlign: 'center' }}>
            Демо-режим: используйте логин-роль (manager, admin…) и пароль «test»
          </p>
        )}
      </div>
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  // ─── Authentication state ───────────────────────────────────────
  //
  // In real (http) mode we first try to restore from localStorage so that
  // a Command+R / F5 refresh does NOT log the operator out.
  //
  // In mock mode we auto-login as 'manager' (unchanged demo flow).

  const restoredUser = MOCK_MODE ? null : getPersistedAuthUser<LoginUser>();
  const restoredAvailability = MOCK_MODE
    ? 'off'
    : (getPersistedAvailabilityStatus() as OperatorAvailabilityStatus | null) ?? 'off';

  // Restore persisted view, validated against the user's role.
  // In mock mode we always start from 'operator' (existing demo behaviour).
  const restoredView: AppView = MOCK_MODE
    ? 'operator'
    : resolveInitialView(
        (restoredUser?.role as RoleCode) ?? 'manager',
        getPersistedCurrentView()
      );

  // If a token is present in localStorage the API module already loaded it
  // (initialised at module evaluation time).  We just need to tell React.
  const [authUser, setAuthUser] = useState<LoginUser | null>(restoredUser);
  // authLoading: true while mock auto-login is in flight; always false in real mode.
  const [authLoading, setAuthLoading] = useState(MOCK_MODE);

  const [activeId, setActiveId] = useState<string>('');
  // Req 3: per-user theme — key is ohub.theme.<userId>
  // Load theme for the current user from localStorage (or default 'dark').
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      const userId = restoredUser?.id ?? '';
      if (userId) {
        const val = localStorage.getItem(`ohub.theme.${userId}`);
        if (val === 'light' || val === 'dark') return val;
      }
      // Fallback: global key for backward compat
      const global = localStorage.getItem('ohub.theme');
      if (global === 'light' || global === 'dark') return global;
    } catch { /* ignore */ }
    return 'dark';
  });
  const [view, setView] = useState<AppView>(restoredView);
  const [supervisorRole, setSupervisorRole] = useState<RoleCode>('shop_supervisor');
  const [availability, setAvailability] = useState<OperatorAvailabilityStatus>(restoredAvailability);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);

  // In mock mode: auto-login as 'manager' so existing demo flow is unchanged.
  // In real mode: if we restored a user from localStorage, skip the login screen;
  //               optionally re-validate the token against the backend.
  useEffect(() => {
    if (!MOCK_MODE) {
      // Real mode: if token + user were restored, try to re-confirm availability
      // from the backend (best-effort — localStorage value is authoritative for UI).
      if (restoredUser && restoredAvailability !== 'off') {
        fetchOperatorAvailability(restoredUser.id)
          .then((res) => {
            // Only update if backend knows this operator and has a different status.
            // Backend may have lost the record after a restart — keep localStorage value.
            if (res.data.status && res.data.status !== 'off') {
              setAvailability(res.data.status);
              persistAvailabilityStatus(res.data.status);
            }
          })
          .catch(() => {
            // Backend unreachable or operator not found — keep persisted value; no-op.
          });
      }
      setAuthLoading(false);
      return;
    }
    // Mock mode auto-login
    loginAs('manager', 'test')
      .then((res) => {
        setAuthUser(res.data.user as LoginUser);
      })
      .catch(() => {
        // Ignore: backend may be off; mock mode still renders the panel
        setAuthUser({ id: 'mock-manager', name: 'manager', role: 'manager', shopId: null, shopName: null });
      })
      .finally(() => setAuthLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Change the active view and persist the choice so a page reload
   * returns the user to the same panel.
   * In mock mode we skip persistence (demo flow is unchanged).
   */
  function handleSetView(nextView: AppView) {
    // RBAC guard: only allow navigation to views the current role is permitted to see.
    // In mock mode the check still applies so demo is consistent with production.
    const allowed = getAllowedViews(currentRole);
    if (!allowed.includes(nextView)) {
      // Silently redirect to the role's default view instead of crashing.
      const safeView = defaultViewForRole(currentRole);
      setView(safeView);
      if (!MOCK_MODE) persistCurrentView(safeView);
      return;
    }
    setView(nextView);
    if (!MOCK_MODE) {
      persistCurrentView(nextView);
    }
  }

  function handleLogin(user: LoginUser, _token: string) {
    // Overwrite any stale persisted user so old name/role never leaks into new session.
    persistAuthUser(user);
    setAuthUser(user);
    // token was already stored in hubApi via loginAs → setAuthToken (and in localStorage)
    // Reset availability to 'off' on fresh login (operator has not started their shift yet)
    setAvailability('off');
    persistAvailabilityStatus('off');
    // Set view to role-based default for the newly logged-in user.
    // (No stale persisted view from a previous session should carry over.)
    const defaultView = defaultViewForRole(user.role as RoleCode);
    setView(defaultView);
    persistCurrentView(defaultView);
    // Clear previous session's stats so stale rows (e.g. Ирина П.) are never shown after re-login.
    setEmployeeStatsResp(null);
    // Req 3: load per-user theme for newly logged in user
    try {
      const userId = user.id;
      const userTheme = userId ? localStorage.getItem(`ohub.theme.${userId}`) : null;
      if (userTheme === 'light' || userTheme === 'dark') {
        setTheme(userTheme);
      } else {
        setTheme('dark'); // default for new users
      }
    } catch { /* ignore */ }
  }

  function handleLogout() {
    logoutLocal(); // calls clearPersistedSession() internally — also clears ohub.currentView
    setAuthUser(null);
    setAvailability('off');
    setView('operator'); // reset to safe default so login screen is clean
    // Clear stats so stale rows are not visible if a different user logs in next.
    setEmployeeStatsResp(null);
  }

  // The current role is derived from authUser when authenticated, or defaults to 'manager'
  const currentRole: RoleCode = (authUser?.role as RoleCode) ?? 'manager';

  async function handleAvailability(action: 'start_work' | 'break' | 'end_work'): Promise<void> {
    setAvailabilityBusy(true);
    try {
      const res = await setOperatorAvailability({
        operatorId: authUser?.id ?? `ui-${currentRole}`,
        operatorName: authUser?.name ?? currentRole,
        action,
      });
      setAvailability(res.data.status);
      // Persist so that a page refresh restores the correct shift status.
      // On 'end_work' we store 'off' which is correct — the operator is no longer on shift.
      persistAvailabilityStatus(res.data.status);
      showStatus(
        action === 'start_work'
          ? 'Смена начата: вы доступны для новых обращений'
          : action === 'break'
            ? 'Перерыв: новые обращения временно не распределяются'
            : 'Смена завершена: вы недоступны для новых обращений'
      );
    } finally {
      setAvailabilityBusy(false);
    }
  }

  const [slaByDialog, setSlaByDialog] = useState<Record<string, { overdue: boolean; status: string; waitingMinutes: number }>>({});
  const [employeeStatsResp, setEmployeeStatsResp] = useState<EmployeeStatsResponse | null>(null);
  // statsLoading: true while the first fetch (or a retry) is in-flight.
  // statsError: set when the fetch failed and no data is available.
  // In MOCK_MODE both remain false/null since mock never fails.
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Data loaded from the API layer (mock or real HTTP)
  const [dialogs, setDialogs] = useState<OperatorDialog[]>([]);
  const [messages, setMessages] = useState<OperatorMessage[]>([]);
  const [loading, setLoading] = useState(true);

  // Client card for the currently active dialog (has clean sourceLabel / pageUrl fields)
  const [activeClientCard, setActiveClientCard] = useState<OperatorClientCard | null>(null);

  // Reply box
  const [replyText, setReplyText] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);

  // Inline action status
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const actionStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showStatus(msg: string) {
    setActionStatus(msg);
    if (actionStatusTimer.current) clearTimeout(actionStatusTimer.current);
    actionStatusTimer.current = setTimeout(() => setActionStatus(null), 4000);
  }

  // Load dashboard aggregate after login
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const res = await fetchDashboard();
        if (cancelled) return;
        const dlgs: OperatorDialog[] = res.data.dialogs ?? [];
        setDialogs(dlgs);
        if (dlgs.length > 0) setActiveId(dlgs[0].id);
      } catch {
        // /dashboard not available — load dialogs separately
        try {
          const res = await fetchDialogs();
          if (cancelled) return;
          const dlgs: OperatorDialog[] = (Array.isArray(res.data) ? res.data : []) as OperatorDialog[];
          setDialogs(dlgs);
          if (dlgs.length > 0) setActiveId(dlgs[0].id);
        } catch {
          if (cancelled) return;
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [authUser]);

  // Reload messages whenever activeId changes
  useEffect(() => {
    let cancelled = false;
    if (!activeId || !authUser) return;
    fetchMessages(activeId).then((res) => {
      if (!cancelled) setMessages(res.data);
    });
    return () => { cancelled = true; };
  }, [activeId, authUser]);

  // Reload client card whenever activeId changes (provides clean sourceLabel / pageUrl)
  useEffect(() => {
    let cancelled = false;
    if (!activeId || !authUser) { setActiveClientCard(null); return; }
    fetchClientCard(activeId).then((res) => {
      if (!cancelled) setActiveClientCard(res.data ?? null);
    }).catch(() => { if (!cancelled) setActiveClientCard(null); });
    return () => { cancelled = true; };
  }, [activeId, authUser]);

  // G) Auto-refresh: poll GET /operator/dialogs every 4 seconds while logged in
  useEffect(() => {
    if (!authUser) return;
    const POLL_INTERVAL_MS = 4000;

    const poll = async () => {
      try {
        const res = await fetchDialogs();
        const freshDialogs: OperatorDialog[] = Array.isArray(res.data) ? res.data : [];
        setDialogs((prev) => {
          // Preserve selected dialog if still exists
          return freshDialogs;
        });
        // Refresh messages for active dialog
        if (activeId) {
          const msgRes = await fetchMessages(activeId);
          setMessages(msgRes.data);
        }
      } catch {
        // ignore polling errors silently
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, activeId]);

  // E) Fetch employee stats — pass authUser context so mock mode returns correct scope
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    // On every authUser change (login/logout), reset stats state.
    setEmployeeStatsResp(null);
    setStatsError(null);
    setStatsLoading(true);

    const load = (isInitial: boolean) =>
      fetchEmployeeStats(authUser.role, authUser.name, authUser.shopName ?? undefined)
        .then((resp) => {
          if (cancelled) return;
          // In HTTP mode: reject any response whose source is 'mock' or whose
          // data contains known seed names when we are NOT in mock mode.
          // This prevents stale mock-derived state from ever rendering.
          const resp2: EmployeeStatsResponse = !MOCK_MODE
            ? {
                ...resp,
                data: resp.data.filter((s) => !SEED_OPERATOR_NAMES.has(s.operatorName)),
                rows: resp.rows.map((row) => ({
                  ...row,
                  employees: row.employees.filter((e) => !SEED_OPERATOR_NAMES.has(e.operatorName)),
                })),
              }
            : resp;
          setEmployeeStatsResp(resp2);
          setStatsError(null);
          if (isInitial) setStatsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          if (isInitial) {
            setStatsLoading(false);
            setStatsError(
              err instanceof Error ? err.message : 'Ошибка загрузки статистики'
            );
          }
          // On poll failure, keep last successful data — do not clear it.
        });

    load(true);
    const timer = setInterval(() => load(false), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authUser]);

  // Reload SLA badges when the current role changes
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    fetchQueue(currentRole).then((res) => {
      if (cancelled) return;
      const map: Record<string, { overdue: boolean; status: string; waitingMinutes: number }> = {};
      for (const item of res.data.items) {
        map[item.id] = {
          overdue: item.slaOverdue,
          status: item.queueStatus,
          waitingMinutes: item.waitingMinutes,
        };
      }
      setSlaByDialog(map);
    });
    return () => {
      cancelled = true;
    };
  }, [authUser, currentRole]);

  const activeDialog = useMemo(
    () => dialogs.find((d) => d.id === activeId) ?? dialogs[0],
    [dialogs, activeId]
  );

  function channelDisplayName(code: string): string {
    return code.charAt(0).toUpperCase() + code.slice(1);
  }

  function channelTone(code: string): string {
    return code.toLowerCase();
  }

  // Req 1: exact arrival time helper
  function formatArrivalTime(isoString: string | null | undefined): string {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (isToday) return `Поступил: ${hh}:${mm}`;
    const day = String(d.getDate()).padStart(2, '0');
    const mon = String(d.getMonth() + 1).padStart(2, '0');
    return `Поступил: ${day}.${mon} ${hh}:${mm}`;
  }

  // ─── Action handlers ────────────────────────────────────────────────────────

  async function handleAssign() {
    if (!activeDialog) return;
    // Pass both name and id so the backend can derive a numeric crmManagerId from the JWT
    // (e.g. hub-account id=15744 is used as managerId in CRM calculation creation)
    const dialogId = activeDialog.id;
    const res = await assignDialog(
      dialogId,
      authUser?.name ?? 'Я (текущий оператор)',
      authUser?.id ?? null
    );
    setDialogs((prev) => prev.map((d) => d.id === res.data.id ? { ...d, ...res.data } : d));
    showStatus('Диалог взят в работу');

    // Immediately refresh client card (status update); then retry after ~2.5 s
    // so the async CRM calculation creation result is reflected in the card.
    fetchClientCard(dialogId)
      .then((cardRes) => setActiveClientCard(cardRes.data ?? null))
      .catch(() => { /* non-fatal */ });
    // Delayed retry — CRM calculation is created asynchronously after assign.
    // Poll twice (2.5 s and 6 s) to catch fast and slow CRM responses.
    const scheduleCardRefresh = (delayMs: number) =>
      setTimeout(() => {
        fetchClientCard(dialogId)
          .then((cardRes) => {
            if (cardRes.data) setActiveClientCard(cardRes.data);
            // Also update the dialog in the list with latest crmCalculationId
            setDialogs((prev) =>
              prev.map((d) =>
                d.id === dialogId
                  ? { ...d, crmCalculationId: cardRes.data?.crmCalculationId ?? d.crmCalculationId,
                           crmCalculationSource: cardRes.data?.crmCalculationSource ?? d.crmCalculationSource,
                           crmCreateError: cardRes.data?.crmCreateError ?? d.crmCreateError }
                  : d
              )
            );
          })
          .catch(() => { /* non-fatal */ });
      }, delayMs);
    scheduleCardRefresh(2500);
    scheduleCardRefresh(6000);
  }

  async function handleSend() {
    if (!activeDialog || !replyText.trim()) return;
    setSendingMsg(true);
    try {
      const res = await sendMessage(activeDialog.id, replyText.trim());
      setMessages((prev) => [...prev, res.data]);
      setReplyText('');
      showStatus('Сообщение отправлено');
    } finally {
      setSendingMsg(false);
    }
  }

  async function handleEscalate() {
    if (!activeDialog) return;
    try {
      const res = await escalateDialog(activeDialog.id);
      setDialogs((prev) => prev.map((d) => d.id === res.data.id ? { ...d, ...res.data } : d));
      // Refresh client card to reflect updated status
      fetchClientCard(activeDialog.id)
        .then((cardRes) => setActiveClientCard(cardRes.data ?? null))
        .catch(() => { /* ignore */ });
      showStatus(`Эскалация → ${res.data.assignedOperatorName}`);
    } catch (err) {
      showStatus('Ошибка эскалации');
    }
  }

  async function handleClose() {
    if (!activeDialog) return;
    const res = await closeDialog(activeDialog.id, 'resolved');
    setDialogs((prev) => prev.map((d) => d.id === res.data.id ? { ...d, ...res.data } : d));
    showStatus('Диалог закрыт');
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Show spinner while mock auto-login resolves
  if (authLoading) {
    return (
      <div className="app-shell" data-theme={theme} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ opacity: 0.5 }}>Загрузка…</p>
      </div>
    );
  }

  // Show login screen when not authenticated (always in real mode; never in mock mode)
  if (!MOCK_MODE && !authUser) {
    return (
      <div className="app-shell" data-theme={theme}>
        <LoginScreen onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <div className="app-shell" data-theme={theme}>
      <a className="skip-link" href="#main">
        Перейти к содержанию
      </a>

      <aside className="sidebar" aria-label="Основная навигация">
        <div className="brand">
          <img src="./grace-logo.jpg" alt="Логотип заказчика" className="brand-logo" />
          <div>
            <p className="eyebrow">CRM Hub</p>
            <strong>Омниканальный хаб</strong>
          </div>
        </div>

        <nav className="nav-list" aria-label="Представления">
          {/* Role-filtered navigation: each role sees only its own panel tab.
              getAllowedViews() returns exactly one view per role in production. */}
          {getAllowedViews(currentRole).includes('operator') && (
            <button
              className={`nav-item ${view === 'operator' ? 'active' : ''}`}
              onClick={() => handleSetView('operator')}
              data-testid="button-view-operator"
            >
              <Headphones size={18} />
              Оператор
            </button>
          )}
          {getAllowedViews(currentRole).includes('supervisor') && (
            <button
              className={`nav-item ${view === 'supervisor' ? 'active' : ''}`}
              onClick={() => handleSetView('supervisor')}
              data-testid="button-view-supervisor"
            >
              <LayoutDashboard size={18} />
              Руководитель
            </button>
          )}
          {getAllowedViews(currentRole).includes('admin') && (
            <button
              className={`nav-item ${view === 'admin' ? 'active' : ''}`}
              onClick={() => handleSetView('admin')}
              data-testid="button-view-admin"
            >
              <Settings size={18} />
              Администратор
            </button>
          )}
        </nav>

        {view === 'supervisor' && (
          <div className="sidebar-subnav" data-testid="supervisor-role-picker">
            <p className="eyebrow">Роль руководителя</p>
            {/* Only show working supervisor role in UI; service_supervisor and founder hidden */}
            {(['shop_supervisor'] as RoleCode[]).map((r) => (
              <button
                key={r}
                className={`nav-item small ${supervisorRole === r ? 'active' : ''}`}
                onClick={() => setSupervisorRole(r)}
                data-testid={`button-supervisor-role-${r}`}
              >
                {r === 'shop_supervisor' && <><Store size={14} /> Магазин</>}
              </button>
            ))}
          </div>
        )}

        {/* Show demo note only in mock mode */}
        {MOCK_MODE && (
          <div className="sidebar-note">
            <ShieldCheck size={18} />
            <span>Демо-режим. Данные на экране тестовые.</span>
          </div>
        )}

        {/* Req 3: Theme toggle in sidebar — works for all roles (operator/supervisor/admin) */}
        <button
          className="ghost-button compact"
          style={{ margin: '8px 12px 0', justifyContent: 'flex-start', gap: '6px' }}
          onClick={() => {
            const nextTheme = theme === 'dark' ? 'light' : 'dark';
            setTheme(nextTheme);
            try {
              const userId = authUser?.id ?? '';
              if (userId) localStorage.setItem(`ohub.theme.${userId}`, nextTheme);
              localStorage.setItem('ohub.theme', nextTheme);
            } catch { /* ignore */ }
          }}
          aria-label="Переключить тему"
          data-testid="button-theme-toggle-sidebar"
        >
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          {theme === 'dark' ? 'Светлая' : 'Тёмная'}
        </button>

        {/* Logged-in user info + logout (shown in real mode) */}
        {!MOCK_MODE && authUser && (
          <div className="sidebar-user-info" data-testid="sidebar-user-info">
            <UserRound size={16} />
            <div>
              <p className="user-name" data-testid="user-name">{authUser.name}</p>
              <p className="user-role" data-testid="user-role">{authUser.role}</p>
            </div>
            <button
              className="ghost-button compact"
              onClick={handleLogout}
              title="Выйти"
              data-testid="button-logout"
              aria-label="Выйти"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </aside>

      <main id="main" className="workspace">
        {/* Role-gated rendering: even if `view` state somehow contains a panel
            the current role cannot access, we refuse to render it. This is a
            belt-and-suspenders guard in addition to the nav filtering above. */}
        {view === 'supervisor' && getAllowedViews(currentRole).includes('supervisor') && (
          <SupervisorView
            role={supervisorRole}
            authRole={(authUser?.role as RoleCode) ?? null}
            authName={authUser?.name ?? null}
            authShopName={authUser?.shopName ?? null}
            authUserId={authUser?.id ?? null}
            availability={availability}
            availabilityBusy={availabilityBusy}
            onAvailability={handleAvailability}
          />
        )}
        {view === 'admin' && getAllowedViews(currentRole).includes('admin') && <AdminView />}
        {view === 'operator' && getAllowedViews(currentRole).includes('operator') && (
        <>
        <header className="topbar">
          <div>
            <p className="eyebrow">
              Рабочее место · {authUser ? authUser.name : currentRole}
              {authUser?.shopName ? ` · ${authUser.shopName}` : ''}
            </p>
            <h1>Рабочее место оператора</h1>
          </div>
          <div className="topbar-actions">
            {/* "Войти как" role selector — only in mock/demo mode */}
            {MOCK_MODE && (
              <label className="login-picker" data-testid="login-picker">
                <span>Войти как</span>
                <select
                  value={currentRole}
                  onChange={(e) => {
                    const role = e.target.value as RoleCode;
                    // In mock mode, update authUser to reflect the newly selected role
                    // and immediately navigate to that role's default panel.
                    setAuthUser((prev) => prev ? { ...prev, role } : null);
                    const newDefaultView = defaultViewForRole(role);
                    setView(newDefaultView);
                  }}
                  data-testid="select-current-role"
                >
                  {/* Only working roles shown in UI */}
                  <option value="manager">Оператор</option>
                  <option value="shop_supervisor">Руководитель</option>
                  <option value="admin">Администратор</option>
                </select>
              </label>
            )}

            {/* Availability buttons (start work / break / end work) */}
            <div
              className="availability-controls"
              data-testid="availability-controls"
              data-availability={availability}
            >
              <span className={`availability-badge ${availability}`} data-testid="availability-status">
                {availability === 'available'
                  ? 'На смене'
                  : availability === 'break'
                    ? 'Перерыв'
                    : 'Не на смене'}
              </span>
              <button
                className="ghost-button compact"
                type="button"
                onClick={() => handleAvailability('start_work')}
                disabled={availabilityBusy || availability === 'available'}
                data-testid="button-availability-start"
                title="Начать работу — стать доступным для новых обращений"
              >
                Начать работу
              </button>
              <button
                className="ghost-button compact"
                type="button"
                onClick={() => handleAvailability('break')}
                disabled={availabilityBusy || availability !== 'available'}
                data-testid="button-availability-break"
                title="Перерыв — остаётесь залогинены, но новые обращения не приходят"
              >
                Перерыв
              </button>
              <button
                className="ghost-button compact"
                type="button"
                onClick={() => handleAvailability('end_work')}
                disabled={availabilityBusy || availability === 'off'}
                data-testid="button-availability-end"
                title="Закончить работу"
              >
                Закончить работу
              </button>
            </div>

            {/* Theme toggle moved to sidebar — no duplicate in topbar */}
            {/* «Новый исходящий чат» — только в демо-режиме; в production (VITE_HUB_API_MODE=http) не показываем */}
            {MOCK_MODE && (
              <button className="primary-button" data-testid="button-create-dialog">
                <Send size={18} />
                Новый исходящий чат
              </button>
            )}
          </div>
        </header>

        <section className="main-grid">
          {/* ─── Dialog list ───────────────────────────────────────────── */}
          <section className="panel dialog-list-panel" aria-labelledby="dialogs-title">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Очередь</p>
                {/* Title reflects split view: own dialogs + new available ones */}
                <h2 id="dialogs-title">Мои диалоги и новые обращения</h2>
              </div>
            </div>

            <div className="dialog-list">
              {loading && (
                <div className="empty-state">
                  <p>Загрузка…</p>
                </div>
              )}
              {!loading && dialogs.length === 0 && (
                <div className="empty-state" data-testid="dialogs-empty-state">
                  <p>Диалогов пока нет</p>
                </div>
              )}
              {!loading && dialogs.length > 0 && (() => {
                // Backend returns _queue: 'mine' | 'available' when JWT auth is active.
                // When _queue is absent (demo/role-fallback mode), render all dialogs
                // in a single section for backward compatibility.
                type DialogWithQueue = OperatorDialog & { _queue?: 'mine' | 'available' };
                const hasQueueMeta = (dialogs as DialogWithQueue[]).some((d) => d._queue != null);

                // Shared render function for a single dialog card
                const renderCard = (dialog: OperatorDialog) => (
                  <button
                    key={dialog.id}
                    className={`dialog-card ${dialog.id === activeId ? 'selected' : ''}`}
                    onClick={() => setActiveId(dialog.id)}
                    data-testid={`button-dialog-${dialog.id}`}
                  >
                    <div className="dialog-card-top">
                      <strong>{dialog.clientName}</strong>
                      <span className={`channel-badge ${channelTone(dialog.channel)}`}>
                        {channelDisplayName(dialog.channel)}
                      </span>
                    </div>
                    <p>{dialog.statusLabel}</p>
                    <div className="dialog-meta">
                      <span>{dialog.shopName}</span>
                      {/* Req 1: exact arrival time instead of relative label.
                           Never use dialog.elapsedLabel (may contain 'только что' or other
                           relative strings). If createdAt is absent/invalid, show 'Поступил: —'. */}
                      <span style={{ fontSize: '0.78rem' }}>
                        {formatArrivalTime(dialog.createdAt) || 'Поступил: —'}
                      </span>
                    </div>
                    {/* F) SLA timer for new/queued dialogs */}
                    {dialog.status !== 'closed' && dialog.status !== 'assigned' && dialog.createdAt && (() => {
                      const sla = dialog.slaStatus;
                      const waiting = dialog.waitingSeconds ?? 0;
                      const mins = Math.floor(waiting / 60);
                      const secs = waiting % 60;
                      const label = mins > 0 ? `${mins}:${String(secs).padStart(2,'0')}` : `${secs}с`;
                      return (
                        <span
                          className={`sla-timer sla-${sla ?? 'ok'}`}
                          data-testid={`sla-timer-${dialog.id}`}
                          title={sla === 'breached' ? `SLA превышен: ожидание ${label}` : `Ожидание: ${label}`}
                        >
                          {sla === 'breached' ? `⏰ ${label}` : `${label}`}
                        </span>
                      );
                    })()}
                    {slaByDialog[dialog.id]?.overdue && (
                      <span
                        className="sla-badge overdue"
                        data-testid={`sla-overdue-${dialog.id}`}
                        title={`Без ответа ${slaByDialog[dialog.id].waitingMinutes} мин — превышен SLA`}
                      >
                        SLA
                      </span>
                    )}
                    {dialog.unreadCount > 0 && (
                      <span className="unread">{dialog.unreadCount}</span>
                    )}
                  </button>
                );

                if (!hasQueueMeta) {
                  // Legacy / demo mode: all dialogs in a single flat list
                  return <>{dialogs.map(renderCard)}</>;
                }

                const myDialogs = (dialogs as DialogWithQueue[]).filter((d) => d._queue === 'mine');
                const availableDialogs = (dialogs as DialogWithQueue[]).filter((d) => d._queue === 'available');

                return (
                  <>
                    {/* Section A: My assigned / own closed history dialogs */}
                    {myDialogs.length > 0 && (
                      <>
                        <div
                          className="dialog-section-label"
                          style={{
                            padding: '6px 12px 2px',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            opacity: 0.55,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                          data-testid="section-label-mine"
                        >
                          Мои диалоги
                        </div>
                        {myDialogs.map(renderCard)}
                      </>
                    )}

                    {/* Section B: Unassigned queued dialogs available to take.
                         These are NOT counted in own stats — they are new incoming requests. */}
                    {availableDialogs.length > 0 && (
                      <>
                        <div
                          className="dialog-section-label"
                          style={{
                            padding: '8px 12px 2px',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            opacity: 0.55,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            borderTop: myDialogs.length > 0 ? '1px solid rgba(128,128,128,0.15)' : undefined,
                            marginTop: myDialogs.length > 0 ? '4px' : undefined,
                          }}
                          data-testid="section-label-available"
                        >
                          Доступно к взятию
                        </div>
                        {availableDialogs.map(renderCard)}
                      </>
                    )}

                    {myDialogs.length === 0 && availableDialogs.length === 0 && (
                      <div className="empty-state" data-testid="dialogs-empty-state">
                        <p>Диалогов пока нет</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </section>

          {/* ─── Conversation panel ────────────────────────────────────── */}
          <section className="panel conversation-panel" aria-labelledby="conversation-title">
            <div className="panel-header">
              <div>
                <p className="eyebrow">{activeDialog?.id ?? '—'}</p>
                <h2 id="conversation-title">{activeDialog?.clientName ?? '—'}</h2>
              </div>
              <div className="dialog-header-actions">
                {activeDialog && (
                  <span className={`channel-badge ${channelTone(activeDialog.channel)}`}>
                    {channelDisplayName(activeDialog.channel)}
                  </span>
                )}
                {/* Assign / take button */}
                {activeDialog && activeDialog.status !== 'assigned' && activeDialog.status !== 'closed' && (
                  <button
                    className="compact-action-btn assign"
                    onClick={handleAssign}
                    data-testid="button-assign-dialog"
                    title="Взять диалог в работу"
                  >
                    <UserRound size={14} />
                    Взять в работу
                  </button>
                )}
              </div>
            </div>

            {/* Action status banner */}
            {actionStatus && (
              <div className="action-status-bar" data-testid="status-action-result" role="status">
                <CheckCircle2 size={14} />
                {actionStatus}
              </div>
            )}

            <div className="message-stack">
              {messages.map((msg) => (
                <article
                  key={msg.id}
                  className={`message ${msg.senderType}`}
                  data-testid={`message-${msg.id}`}
                >
                  <p>{msg.text}</p>
                  <span>{msg.timeLabel}</span>
                </article>
              ))}
            </div>

            {/* Reply box */}
            <div className="reply-box">
              <label htmlFor="reply">Ответ оператора</label>
              <div className="reply-row">
                <input
                  id="reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Введите ответ…"
                  disabled={sendingMsg || activeDialog?.status === 'closed'}
                  data-testid="input-reply-text"
                />
                <button
                  className="primary-button compact"
                  onClick={handleSend}
                  disabled={sendingMsg || !replyText.trim() || activeDialog?.status === 'closed'}
                  data-testid="button-send-reply"
                >
                  Отправить
                </button>
              </div>
            </div>

            {/* Action row: Escalate / Close */}
            <div className="dialog-actions-row" data-testid="dialog-actions-row">
              <button
                className="compact-action-btn escalate"
                onClick={handleEscalate}
                disabled={!activeDialog || activeDialog.status === 'closed'}
                data-testid="button-escalate-dialog"
                title="Передать диалог руководителю магазина"
              >
                <Route size={14} />
                Эскалация
              </button>
              <button
                className="compact-action-btn close-dialog"
                onClick={handleClose}
                disabled={!activeDialog || activeDialog.status === 'closed'}
                data-testid="button-close-dialog"
                title="Закрыть диалог"
              >
                <Store size={14} />
                Закрыть
              </button>
            </div>
          </section>

          {/* ─── Client card ──────────────────────────────────────────── */}
          <aside className="side-stack">
            {activeDialog && (
              <section className="panel client-panel" aria-labelledby="client-title">
                <div className="panel-header">
                  <div>
                    <p className="eyebrow">Карточка</p>
                    <h2 id="client-title">Карточка клиента</h2>
                  </div>
                  <CheckCircle2 size={20} className="ok-icon" />
                </div>
                <dl className="details-list">
                  <div>
                    <dt>Имя</dt>
                    <dd data-testid="text-client-name">{activeClientCard?.name ?? activeDialog.clientName}</dd>
                  </div>
                  <div>
                    <dt>Телефон</dt>
                    <dd data-testid="text-client-phone">{activeClientCard?.phone ?? activeDialog.phone}</dd>
                  </div>
                  <div>
                    <dt>Канал</dt>
                    <dd data-testid="text-client-channel">{channelDisplayName(activeDialog.channel)}</dd>
                  </div>
                  <div>
                    <dt>Магазин</dt>
                    <dd data-testid="text-client-shop">{activeClientCard?.shopName ?? activeDialog.shopName}</dd>
                  </div>
                  <div>
                    <dt>Источник</dt>
                    {/* Show human-readable sourceLabel from client card; never show internal routing keys */}
                    <dd data-testid="text-client-source">
                      {activeClientCard?.sourceLabel ?? activeDialog.source}
                    </dd>
                  </div>
                  {/* Separate page/site URL field if available */}
                  {(activeClientCard?.pageUrl) && (
                    <div>
                      <dt>Сайт</dt>
                      <dd data-testid="text-client-page-url">
                        <a href={activeClientCard.pageUrl} target="_blank" rel="noopener noreferrer">
                          {activeClientCard.pageUrl}
                        </a>
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>Расчёт CRM</dt>
                    <dd data-testid="text-client-crm-id">
                      {(() => {
                        const calcId = activeClientCard?.crmCalculationId ?? activeDialog.crmCalculationId;
                        const source = activeClientCard?.crmCalculationSource ?? activeDialog.crmCalculationSource;
                        const err = activeClientCard?.crmCreateError ?? activeDialog.crmCreateError;
                        if (calcId != null) {
                          if (source === 'found') return `Расчёт найден: #${calcId}`;
                          if (source === 'created') return `Расчёт создан: #${calcId}`;
                          return `#${calcId}`;
                        }
                        if (err) {
                          const shortErr = err.length > 60 ? err.slice(0, 57) + '…' : err;
                          return `Ошибка CRM: ${shortErr}`;
                        }
                        // For assigned/in-progress dialogs: CRM creation is async.
                        // Show a pending state instead of misleading 'Не найден'.
                        const dialogStatus = activeDialog.status;
                        if (dialogStatus === 'assigned' || dialogStatus === 'escalated') {
                          return 'Ожидает CRM…';
                        }
                        return 'Не найден';
                      })()}
                    </dd>
                  </div>
                  <div>
                    <dt>Статус</dt>
                    <dd data-testid="text-dialog-status">{activeDialog.statusLabel}</dd>
                  </div>
                </dl>
              </section>
            )}

            {!activeDialog && !loading && (
              <section className="panel client-panel" aria-labelledby="client-title-empty">
                <div className="panel-header">
                  <div>
                    <p className="eyebrow">Карточка</p>
                    <h2 id="client-title-empty">Карточка клиента</h2>
                  </div>
                </div>
                <div className="empty-state">
                  <p>Выберите диалог</p>
                </div>
              </section>
            )}

            {/* E) Employee daily chat count block — title and content are scoped by role.
               Format: "openNow / totalToday" e.g. "2 / 7"
               Own scope:  operator sees only their own row + value.
               Shop scope: supervisor sees per-employee rows + "Итого по магазину" row.
               All scope:  admin/founder sees flat list + grand total. */}
            {/* Render stats panel whenever stats have been fetched.
               * Show for scope=own even when data is empty ("no dialogs today")
               * so the correct operator name is visible and no stale rows appear. */}
            {employeeStatsResp && (() => {
              const scope = employeeStatsResp.scope;
              const statsTitle =
                scope === 'own'
                  ? 'Моя статистика за сегодня'
                  : scope === 'shop'
                  ? 'Статистика магазина за сегодня'
                  : 'Статистика по сотрудникам за сегодня';

              return (
                <section
                  className="panel"
                  aria-labelledby="employee-stats-title"
                  data-testid="employee-stats-panel"
                  data-stats-scope={scope}
                >
                  <div className="panel-header">
                    <div>
                      <p className="eyebrow">Статистика</p>
                      <h2 id="employee-stats-title">{statsTitle}</h2>
                    </div>
                    <UsersRound size={20} />
                  </div>

                  {/* Own scope: single operator row — "openNow / totalToday" */}
                  {/* Trust backend response directly — do NOT filter by authUser.name here.
                      Backend already scopes the query by JWT token; applying an additional
                      name-match would silently drop the row when operatorName != authUser.name
                      (e.g. hub-account whose display name differs from login). */}
                  {scope === 'own' && (() => {
                    const ownRows = employeeStatsResp.data;
                    return (
                      <ul className="telemetry-list" style={{ padding: '0 24px 24px' }}>
                        {ownRows.length === 0 && (
                          <li data-testid="employee-stat-empty">
                            <span style={{ opacity: 0.5 }}>Диалогов сегодня нет</span>
                          </li>
                        )}
                        {ownRows.map((s: EmployeeStat) => (
                          <li key={s.operatorName} data-testid={`employee-stat-${s.operatorName}`}>
                            <strong>{s.operatorName}</strong>
                            <span
                              title="открытых / всего за день"
                              data-testid={`employee-stat-display-${s.operatorName}`}
                            >
                              {s.displayValue}
                            </span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}

                  {/* Shop scope: per-employee rows grouped by shop + "Итого по магазину" */}
                  {scope === 'shop' && (
                    <div style={{ padding: '0 24px 24px' }}>
                      {employeeStatsResp.rows.map((row: ShopStatRow) => (
                        <div key={row.shopName} data-testid={`stats-shop-${row.shopName}`}>
                          {employeeStatsResp.rows.length > 1 && (
                            <p
                              className="eyebrow"
                              style={{ marginBottom: '0.25rem', marginTop: '0.5rem' }}
                            >
                              {row.shopName}
                            </p>
                          )}
                          <ul className="telemetry-list">
                            {row.employees.map((emp: EmployeeStat) => (
                              <li
                                key={emp.operatorName}
                                data-testid={`employee-stat-${emp.operatorName}`}
                              >
                                <strong>{emp.operatorName}</strong>
                                <span
                                  title="открытых / всего за день"
                                  data-testid={`employee-stat-display-${emp.operatorName}`}
                                >
                                  {emp.displayValue}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p
                            className="telemetry-total"
                            style={{
                              fontSize: '0.8rem',
                              opacity: 0.7,
                              marginTop: '0.25rem',
                              fontWeight: 600,
                            }}
                            data-testid={`stats-shop-total-${row.shopName}`}
                          >
                            Итого по магазину: {row.displayValue}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* All scope: flat list + grand total */}
                  {scope === 'all' && (
                    <>
                      <ul className="telemetry-list" style={{ padding: '0 24px 8px' }}>
                        {employeeStatsResp.data.map((s: EmployeeStat) => (
                          <li key={s.operatorName} data-testid={`employee-stat-${s.operatorName}`}>
                            <strong>{s.operatorName}</strong>
                            <span
                              title="открытых / всего за день"
                              data-testid={`employee-stat-display-${s.operatorName}`}
                            >
                              {s.displayValue}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p
                        className="telemetry-total"
                        style={{ padding: '0 24px 16px', fontSize: '0.8rem', opacity: 0.6 }}
                        data-testid="employee-stats-total"
                      >
                        Итого: {employeeStatsResp.displayValue}
                      </p>
                    </>
                  )}
                </section>
              );
            })()}
          </aside>
        </section>
        </>
        )}
      </main>
    </div>
  );
}

export default App;
